export const UNAVAILABLE_STATISTICS_CALCULATORS = [
  "Prior Posterior",
  "Bayes Factor",
  "MCMC",
  "Bootstrap",
  "Monte Carlo",
  "Random Number",
  "Information Gain",
  "Mutual Information",
  "KL Divergence",
  "Cross Entropy",
  "Survival Analysis",
  "Kaplan Meier",
  "ANCOVA",
  "Repeated Measures",
  "Simulation",
  "Weighting",
  "Calibration Weight",
] as const;

type UnavailableStatisticsCalculator =
  (typeof UNAVAILABLE_STATISTICS_CALCULATORS)[number];

const unavailableTitles = new Set<string>(UNAVAILABLE_STATISTICS_CALCULATORS);

const unavailableRelatedCalculators: Record<
  UnavailableStatisticsCalculator,
  { slug: string; context: string }
> = {
  "Prior Posterior": {
    slug: "statistics-bayesian-inference-calculator",
    context: "a supported binary-hypothesis Bayesian calculation",
  },
  "Bayes Factor": {
    slug: "statistics-bayesian-inference-calculator",
    context: "a supported binary-hypothesis Bayesian calculation",
  },
  MCMC: {
    slug: "statistics-bayesian-inference-calculator",
    context: "a supported binary-hypothesis Bayesian calculation",
  },
  Bootstrap: {
    slug: "statistics-mean-calculator",
    context: "a basic descriptive summary",
  },
  "Monte Carlo": {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability model",
  },
  "Random Number": {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability model",
  },
  "Information Gain": {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability model",
  },
  "Mutual Information": {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability model",
  },
  "KL Divergence": {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability model",
  },
  "Cross Entropy": {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability model",
  },
  "Survival Analysis": {
    slug: "statistics-confidence-interval-calculator",
    context: "a supported confidence-interval estimate",
  },
  "Kaplan Meier": {
    slug: "statistics-confidence-interval-calculator",
    context: "a supported confidence-interval estimate",
  },
  ANCOVA: {
    slug: "statistics-mean-calculator",
    context: "a basic descriptive summary",
  },
  "Repeated Measures": {
    slug: "statistics-mean-calculator",
    context: "a basic descriptive summary",
  },
  Simulation: {
    slug: "statistics-probability-calculator",
    context: "a supported exact probability calculation",
  },
  Weighting: {
    slug: "statistics-mean-calculator",
    context: "a basic unweighted descriptive summary",
  },
  "Calibration Weight": {
    slug: "statistics-mean-calculator",
    context: "a basic unweighted descriptive summary",
  },
};

const activeStatisticsDescriptions: Record<string, string> = {
  "Survey Design":
    "Estimate the sample size for a survey from confidence level, expected proportion, margin of error, and an optional finite-population correction.",
  "Response Rate":
    "Calculate survey response rate as completed interviews divided by eligible sampled units, where eligible units equal sampled units minus ineligible units.",
  "Cluster Sample":
    "Estimate the cluster-sampling design effect from average cluster size and intraclass correlation, then compare effective and inflated sample sizes.",
  "Post Stratification":
    "Calculate post-stratification weights and weighted stratum totals from population and sample shares; flag strata with no sampled cases.",
  "Stratified Sample":
    "Allocate a total sample proportionally or by Neyman allocation across strata, using largest-remainder rounding to preserve the requested total.",
  "Quota Sample":
    "Allocate a sample across quota shares with largest-remainder rounding. Quota sampling is non-probability sampling, and no margin of error is computed.",
  "Systematic Sample":
    "Select systematic sample units from a population using a random start and interval, with an explicit floor rule for fractional intervals.",
  "Hazard Ratio":
    "Compare exposed and reference event rates per person-time using an incidence-rate ratio and log-scale 95% confidence interval; this is not a Cox model.",
  "Poisson Distribution":
    "Calculate P(X = k), P(X ≤ k), and P(X ≥ k) with a Poisson count model, alongside its mean, variance, and standard deviation. Assumes independent events at a constant average rate.",
  "Bayesian Inference":
    "Apply Bayes' theorem to a binary hypothesis using a prior probability, sensitivity, and false-positive rate; view the evidence probability, posterior probability, and change from the prior.",
};

export const IMPLEMENTED_STATISTICS_SPECIALIST_TITLES = [
  "Survey Design",
  "Response Rate",
  "Cluster Sample",
  "Post Stratification",
  "Stratified Sample",
  "Quota Sample",
  "Systematic Sample",
  "Hazard Ratio",
] as const;

const implementedStatisticsTitles = new Set<string>(
  IMPLEMENTED_STATISTICS_SPECIALIST_TITLES,
);

export function getStatisticsCalculatorTitle(title: string): string {
  return title.replace(/\s+calculator$/i, "").trim();
}

export function isImplementedStatisticsSpecialist(title: string): boolean {
  return implementedStatisticsTitles.has(getStatisticsCalculatorTitle(title));
}

export function getStatisticsUnavailableCalculatorProfile(title: string) {
  const topic = getStatisticsCalculatorTitle(title);
  if (!unavailableTitles.has(topic)) return null;
  const related = unavailableRelatedCalculators[topic as UnavailableStatisticsCalculator];
  const notice = `This calculator is not available yet. We're building a dedicated version of the ${topic} calculator.`;
  return {
    topic,
    notice,
    seoDescription: `${notice} No input fields or calculated results are shown on this page yet.`,
    includeInteractiveSchemas: false as const,
    related,
  };
}

export function getStatisticsCalculatorDescription(title: string): string | undefined {
  const topic = getStatisticsCalculatorTitle(title);
  const unavailable = getStatisticsUnavailableCalculatorProfile(topic);
  if (unavailable) return unavailable.seoDescription;
  return activeStatisticsDescriptions[topic];
}

export function getStatisticsUnavailableRelatedCalculator(title: string) {
  return getStatisticsUnavailableCalculatorProfile(title)?.related ?? null;
}

export function getStatisticsUnavailableFaqItems(title: string) {
  const profile = getStatisticsUnavailableCalculatorProfile(title);
  if (!profile) return null;
  return [
    {
      question: `Is the ${profile.topic} calculator available?`,
      answer: profile.notice,
    },
    {
      question: `Does this page calculate ${profile.topic}?`,
      answer:
        "No. It currently has no input fields and displays no calculated results.",
    },
  ];
}